import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { getErrorMessage } from '@/lib/api'
import { billingApi } from '@/services/billing'
import { useAuthStore } from '@/stores/auth-store'

// Shows trial-days-remaining + an upgrade CTA on the dashboard, and bounces
// to the upgrade wall if the trial lapses mid-session (login.tsx already
// handles the common case right after sign-in).
export function TrialBanner() {
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const [isRedirecting, setIsRedirecting] = useState(false)

  const billingQuery = useQuery({
    queryKey: ['billing-status'],
    queryFn: billingApi.getStatus,
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  const status = billingQuery.data

  useEffect(() => {
    if (status?.is_locked) {
      navigate('/billing/locked', { replace: true })
    }
  }, [status?.is_locked, navigate])

  if (!status || status.subscription_status !== 'trial' || status.is_locked) {
    return null
  }

  const daysRemaining = status.trial_days_remaining ?? null

  const onUpgrade = async () => {
    setIsRedirecting(true)
    try {
      const { checkout_url } = await billingApi.createCheckoutSession()
      window.location.href = checkout_url
    } catch (error) {
      toast.error(getErrorMessage(error))
      setIsRedirecting(false)
    }
  }

  return (
    <div className="mb-4 flex flex-col items-start justify-between gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 sm:flex-row sm:items-center">
      <p className="text-sm text-foreground">
        {daysRemaining === 0
          ? 'Your free trial ends today.'
          : `${daysRemaining ?? ''} day${daysRemaining === 1 ? '' : 's'} left in your free trial.`}
      </p>
      <Button type="button" size="sm" disabled={isRedirecting} onClick={onUpgrade}>
        Upgrade now
      </Button>
    </div>
  )
}
